/**
 * Serviço de Impressão Térmica USB via WebUSB
 * Compatível com Epson TM-T88V e impressoras ESC/POS
 * Auto-reconnect habilitado para impressão automática
 *
 * === FASE 6: Fila de Impressão Segura ===
 * === FASE 5: Integração com PrinterSettings do Banco ===
 * === FASE 4: 3ª Via de Expedição ===
 * === FASE 1: Orquestrador Único de Impressão ===
 */

class ThermalPrinterService {
  constructor() {
    this.device = null;
    this.endpoint = null;
    this.isConnected = false;
    this.paperWidth = 80; // mm
    this.charsPerLine = 48; // Largura real da impressora 80mm
    this.copies = this.loadCopies();
    this.autoReconnect = true;
    this.reconnectInterval = null;

    // Epson TM-T88V IDs
    this.EPSON_VENDOR_ID = 0x04B8;
    this.EPSON_TM_T88V_PRODUCT_ID = 0x0202;

    // ========== FASE 6: Fila de Impressão ==========
    this._printQueue = [];
    this._isPrinting = false;

    // ========== FASE 5: Cache de Settings da API ==========
    this._apiSettings = null;
    this._settingsLoaded = false;
    this._settingsLoadPromise = null;

    // ========== Web Serial API (alternativa ao WebUSB) ==========
    this.serialPort = null;
    this.serialWriter = null;
    this.connectionType = null; // 'usb' | 'serial' | null
  }

  // ========== FASE 6: FILA DE IMPRESSÃO / CONTROLE DE CONFLITO ==========

  /**
   * Enfileira uma função de impressão para execução segura.
   * Garante que apenas uma impressão ocorra por vez no WebUSB.
   */
  enqueuePrint(printFn) {
    return new Promise((resolve, reject) => {
      this._printQueue.push({ fn: printFn, resolve, reject });
      this._processQueue();
    });
  }

  async _processQueue() {
    if (this._isPrinting || this._printQueue.length === 0) return;
    this._isPrinting = true;

    const { fn, resolve, reject } = this._printQueue.shift();
    try {
      const result = await fn();
      resolve(result);
    } catch (error) {
      reject(error);
    } finally {
      this._isPrinting = false;
      if (this._printQueue.length > 0) {
        setTimeout(() => this._processQueue(), 400);
      }
    }
  }

  // ========== FASE 5: SETTINGS DO BANCO ==========

  /**
   * Carrega configurações de impressão da API (DeliveryAutoConfig).
   * Usa cache para não bater na API a cada impressão.
   */
  async loadSettings() {
    if (this._settingsLoaded && this._apiSettings) return this._apiSettings;
    if (this._settingsLoadPromise) return this._settingsLoadPromise;

    this._settingsLoadPromise = (async () => {
      try {
        const userStr = localStorage.getItem('user');
        if (!userStr) return null;
        const user = JSON.parse(userStr);
        const storeId = user.store_id;
        if (!storeId) return null;

        const response = await fetch(`/api/delivery-config/${storeId}`);
        if (response.ok) {
          this._apiSettings = await response.json();
          this._settingsLoaded = true;
          console.log('[Printer] Settings carregadas da API:', this._apiSettings);
          return this._apiSettings;
        }
      } catch (e) {
        console.warn('[Printer] Falha ao carregar settings da API:', e.message);
      }
      return null;
    })();

    const result = await this._settingsLoadPromise;
    this._settingsLoadPromise = null;
    return result;
  }

  /**
   * Retorna as configurações efetivas de impressão.
   * Precedência: API (banco) > localStorage
   */
  getSettings() {
    if (this._apiSettings) {
      return {
        autoPrint: this._apiSettings.auto_print_enabled !== false,
        restaurantCopies: this._apiSettings.print_restaurant_copies || 1,
        deliveryCopies: this._apiSettings.print_delivery_copies || 1,
        restaurantPrinterId: this._apiSettings.restaurant_printer_id || null,
        deliveryPrinterId: this._apiSettings.delivery_printer_id || null,
        source: 'api'
      };
    }
    return {
      autoPrint: localStorage.getItem('autoPrintEnabled') !== 'false',
      restaurantCopies: this.copies,
      deliveryCopies: this.copies,
      restaurantPrinterId: null,
      deliveryPrinterId: null,
      source: 'localStorage'
    };
  }

  /**
   * Invalida o cache de settings (chamar quando o user salvar configs).
   */
  invalidateSettings() {
    this._apiSettings = null;
    this._settingsLoaded = false;
  }

  // ========== CONFIGURAÇÃO DE CÓPIAS (localStorage) ==========

  loadCopies() {
    try {
      const saved = localStorage.getItem('thermalPrinterCopies');
      if (saved !== null) {
        const val = parseInt(saved, 10);
        if (val >= 1 && val <= 5) return val;
      }
    } catch (e) {}
    return 2;
  }

  setCopies(num) {
    const val = Math.max(1, Math.min(5, parseInt(num, 10) || 2));
    this.copies = val;
    try {
      localStorage.setItem('thermalPrinterCopies', val.toString());
    } catch (e) {}
    return val;
  }

  getCopies() {
    return this.copies;
  }

  // ========== WEBUSB: CONEXÃO ==========

  isSupported() {
    return 'usb' in navigator;
  }

  async tryAutoReconnect() {
    if (!this.isSupported() || this.isConnected) return false;

    try {
      const devices = await navigator.usb.getDevices();
      if (devices.length > 0) {
        this.device = devices.find(d => d.vendorId === this.EPSON_VENDOR_ID) || devices[0];

        await this.device.open();

        if (this.device.configuration === null) {
          await this.device.selectConfiguration(1);
        }

        const printerInterface = this.device.configuration.interfaces.find(
          iface => iface.alternates.some(alt =>
            alt.endpoints.some(ep => ep.direction === 'out')
          )
        );

        if (!printerInterface) return false;

        await this.device.claimInterface(printerInterface.interfaceNumber);
        const alternate = printerInterface.alternates[0];
        this.endpoint = alternate.endpoints.find(ep => ep.direction === 'out');

        if (!this.endpoint) return false;

        this.isConnected = true;
        console.log('Impressora auto-reconectada:', this.device.productName);
        return true;
      }
    } catch (e) {
      console.warn('Auto-reconnect falhou:', e.message);
      this.isConnected = false;
    }
    return false;
  }

  startAutoReconnect() {
    if (this.reconnectInterval) return;
    this.reconnectInterval = setInterval(async () => {
      if (!this.isConnected) {
        await this.tryAutoReconnect();
      }
    }, 5000);
    this.tryAutoReconnect();
  }

  stopAutoReconnect() {
    if (this.reconnectInterval) {
      clearInterval(this.reconnectInterval);
      this.reconnectInterval = null;
    }
  }

  async connect() {
    if (!this.isSupported()) {
      throw new Error('WebUSB não é suportado neste navegador. Use Chrome ou Edge.');
    }

    // Detectar modo InPrivate/Incognito
    const isPrivate = await this._detectPrivateMode();
    if (isPrivate) {
      throw new Error(
        'Modo InPrivate/Incognito detectado. ' +
        'O WebUSB não funciona em modo privado. ' +
        'Abra o navegador em modo normal para conectar a impressora.'
      );
    }

    try {
      this.device = await navigator.usb.requestDevice({
        filters: [
          { vendorId: this.EPSON_VENDOR_ID },
          { classCode: 7 },
        ]
      });

      // Tentar abrir o dispositivo com tratamento especial para Access Denied
      try {
        await this.device.open();
      } catch (openError) {
        if (openError.message && openError.message.includes('Access denied')) {
          throw new Error(
            'Acesso negado ao dispositivo USB. Possíveis soluções:\n' +
            '1. Feche outros programas que usem a impressora\n' +
            '2. No Windows: desative o Spooler de Impressão (services.msc > Print Spooler > Parar)\n' +
            '3. Não use modo InPrivate/Incognito\n' +
            '4. Desconecte e reconecte o cabo USB\n' +
            '5. Use o Chrome (não Edge) em modo normal'
          );
        }
        throw openError;
      }

      if (this.device.configuration === null) {
        await this.device.selectConfiguration(1);
      }

      const printerInterface = this.device.configuration.interfaces.find(
        iface => iface.alternates.some(alt =>
          alt.endpoints.some(ep => ep.direction === 'out')
        )
      );

      if (!printerInterface) {
        throw new Error('Interface de impressão não encontrada');
      }

      try {
        await this.device.claimInterface(printerInterface.interfaceNumber);
      } catch (claimError) {
        if (claimError.message && claimError.message.includes('denied')) {
          throw new Error(
            'Interface USB já está em uso. ' +
            'Feche outras abas do navegador e tente novamente, ' +
            'ou desative o Spooler de Impressão do Windows.'
          );
        }
        throw claimError;
      }

      const alternate = printerInterface.alternates[0];
      this.endpoint = alternate.endpoints.find(ep => ep.direction === 'out');

      if (!this.endpoint) {
        throw new Error('Endpoint de saída não encontrado');
      }

      this.isConnected = true;
      this.connectionType = 'usb';
      console.log('[Printer] Conectada via USB:', this.device.productName);

      return {
        success: true,
        name: this.device.productName || 'Impressora Termica USB',
        vendorId: this.device.vendorId,
        productId: this.device.productId,
        connectionType: 'usb'
      };
    } catch (error) {
      console.error('Erro ao conectar impressora:', error);
      this.isConnected = false;
      throw error;
    }
  }

  // Detectar modo InPrivate/Incognito
  async _detectPrivateMode() {
    try {
      if ('storage' in navigator && 'estimate' in navigator.storage) {
        const { quota } = await navigator.storage.estimate();
        if (quota && quota < 120000000) return true;
      }
    } catch (e) {}
    return false;
  }

  // ========== WEB SERIAL API (ALTERNATIVA AO WebUSB) ==========

  isSerialSupported() {
    return 'serial' in navigator;
  }

  /**
   * Conectar via Web Serial API.
   * Funciona com impressoras M244A/TM-T88V no Windows sem conflito de driver.
   * @param {number} baudRate - Taxa de transmissão (padrão 9600)
   */
  async connectSerial(baudRate = 9600) {
    if (!this.isSerialSupported()) {
      throw new Error('Web Serial API não suportada. Use Chrome 89+ ou Edge 89+.');
    }

    try {
      // Solicitar porta serial ao usuário
      this.serialPort = await navigator.serial.requestPort();

      // Abrir com parâmetros ESC/POS padrão
      await this.serialPort.open({
        baudRate: baudRate,
        dataBits: 8,
        stopBits: 1,
        parity: 'none',
        flowControl: 'none',
        bufferSize: 4096
      });

      this.serialWriter = this.serialPort.writable.getWriter();
      this.isConnected = true;
      this.connectionType = 'serial';

      // Tentar ler info do dispositivo
      const info = this.serialPort.getInfo();
      const name = (info.usbVendorId && info.usbProductId)
        ? `Serial USB (VID:${info.usbVendorId.toString(16)} PID:${info.usbProductId.toString(16)})`
        : 'Impressora Serial';

      console.log('[Printer] Conectada via Serial:', name, 'baudRate:', baudRate);

      return {
        success: true,
        name: name,
        vendorId: info.usbVendorId || 0,
        productId: info.usbProductId || 0,
        connectionType: 'serial'
      };
    } catch (error) {
      console.error('[Printer] Erro ao conectar Serial:', error);
      this.serialPort = null;
      this.serialWriter = null;
      this.connectionType = null;
      this.isConnected = false;

      if (error.message && error.message.includes('No port selected')) {
        // Usuário cancelou - não propagar erro
        return { success: false, cancelled: true };
      }
      throw error;
    }
  }

  /**
   * Desconectar porta serial
   */
  async disconnectSerial() {
    try {
      if (this.serialWriter) {
        await this.serialWriter.releaseLock();
        this.serialWriter = null;
      }
      if (this.serialPort) {
        await this.serialPort.close();
        this.serialPort = null;
      }
    } catch (e) {
      console.warn('[Printer] Erro ao desconectar serial:', e);
    }
    this.connectionType = null;
    this.isConnected = false;
  }

  async disconnect() {
    if (this.connectionType === 'serial') {
      await this.disconnectSerial();
      return;
    }
    if (this.device) {
      try {
        await this.device.close();
      } catch (e) {
        console.warn('Erro ao desconectar:', e);
      }
      this.device = null;
      this.endpoint = null;
      this.isConnected = false;
      this.connectionType = null;
    }
  }

  async send(data) {
    const uint8Array = data instanceof Uint8Array ? data : new Uint8Array(data);

    if (this.connectionType === 'serial') {
      if (!this.isConnected || !this.serialWriter) {
        throw new Error('Impressora serial não conectada');
      }
      await this.serialWriter.write(uint8Array);
      return;
    }

    // WebUSB fallback
    if (!this.isConnected || !this.device || !this.endpoint) {
      throw new Error('Impressora não conectada');
    }
    await this.device.transferOut(this.endpoint.endpointNumber, uint8Array);
  }

  // ========== COMANDOS ESC/POS ==========

  ESC = 0x1B;
  GS = 0x1D;
  LF = 0x0A;

  async init() {
    await this.send([this.ESC, 0x40]);       // Reset printer
    await this.send([this.ESC, 0x74, 19]);   // Code Page PC858 (Latin-1 + Euro)
  }

  async feedLines(lines = 1) {
    await this.send([this.ESC, 0x64, lines]);
  }

  async cut(partial = false) {
    await this.send([this.GS, 0x56, partial ? 0x01 : 0x00]);
  }

  async setAlignment(align) {
    await this.send([this.ESC, 0x61, align]);
  }

  async setBold(enabled) {
    await this.send([this.ESC, 0x45, enabled ? 1 : 0]);
  }

  async setTextSize(width = 1, height = 1) {
    const n = ((width - 1) << 4) | (height - 1);
    await this.send([this.GS, 0x21, n]);
  }

  async printText(text) {
    // Sanitizar: remover acentos e chars nao-ASCII para ESC/POS
    const safe = text
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')  // Remove diacriticos (acentos)
      .replace(/\u00a0/g, ' ')                             // Non-breaking space -> space
      .replace(/[^\x20-\x7E\n\r]/g, ' ');               // Qualquer char nao-ASCII -> space
    const encoder = new TextEncoder();
    const bytes = encoder.encode(safe);
    await this.send(bytes);
  }

  async printLine(text = '') {
    await this.printText(text + '\n');
  }

  async printSeparator(char = '-') {
    await this.printLine(char.repeat(this.charsPerLine));
  }

  async printSpacedLine(left, right, effectiveWidth = null) {
    const maxChars = effectiveWidth || this.charsPerLine;
    const l = String(left || '');
    const r = String(right || '');
    const spaces = maxChars - l.length - r.length;
    const line = l + ' '.repeat(Math.max(spaces, 1)) + r;
    await this.printLine(line);
  }

  formatCurrency(value) {
    if (value === null || value === undefined || isNaN(value)) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value).replace(/\u00a0/g, ' ');
  }

  // ============ VIA COMPLETA (RESTAURANTE) ============
  async printOrder(order, storeName = 'Hit Grill') {
    try {
      await this.init();

      // Largura LOCAL da etiqueta de pedido (42 cols = Epson TM-T88V Font A)
      // NAO alterar this.charsPerLine global — outras vias nao sao afetadas
      const COLS = 42;
      const DW_COLS = Math.floor(COLS / 2); // 21 cols em double-width

      // Helper: quebra texto longo em multiplas linhas respeitando palavras
      const wrapLines = (text, maxW) => {
        if (!text || text.length <= maxW) return [text || ''];
        const words = text.split(' ');
        const lines = [];
        let cur = '';
        for (const w of words) {
          if (cur.length + w.length + 1 > maxW) {
            if (cur) lines.push(cur);
            cur = w.length > maxW ? w.substring(0, maxW) : w;
          } else {
            cur = cur ? cur + ' ' + w : w;
          }
        }
        if (cur) lines.push(cur);
        return lines.length ? lines : [''];
      };

      // Helper: imprime texto longo quebrando em linhas de COLS
      const printWrapped = async (text) => {
        const lines = wrapLines(text, COLS);
        for (const ln of lines) await this.printLine(ln);
      };

      // Cabecalho
      await this.setAlignment(1);
      await this.setTextSize(2, 1);
      await this.setBold(true);
      // Store name em double-width: limitar a DW_COLS
      const safeStore = storeName.length > DW_COLS ? storeName.substring(0, DW_COLS) : storeName;
      await this.printLine(safeStore);

      await this.setTextSize(1, 1);
      await this.setBold(false);
      await this.printLine('PEDIDO');

      // Numero do pedido
      await this.setTextSize(2, 1);
      await this.setBold(true);
      const displayId = order.display_id || order.order_number || order.id?.substring(0, 8).toUpperCase() || '';
      await this.printLine('#' + displayId);
      await this.setTextSize(1, 1);
      await this.setBold(false);

      // Canal
      const channel = order.external_source || order.order_channel || 'LOJA';
      const channelText = 'Canal: ' + channel;
      if (channelText.length > COLS) {
        await this.printLine('Canal:');
        await this.printLine(channel.substring(0, COLS));
      } else {
        await this.printLine(channelText);
      }

      await this.setAlignment(0);
      await this.printLine('-'.repeat(COLS));

      // Data/hora
      const date = new Date(order.created_at);
      await this.printSpacedLine(
        date.toLocaleDateString('pt-BR'),
        date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
        COLS
      );

      // Cliente (com wrap se nome longo)
      if (order.customer_name) {
        const clientText = 'Cliente: ' + order.customer_name;
        await printWrapped(clientText);
      }

      // Detectar se eh pedido de delivery
      const deliverySource = order.external_source || order.order_channel || '';
      const isDeliveryOrder = ['IFOOD', '99FOOD', 'KEETA', 'CARDAPIO_DIGITAL', 'DELIVERY'].includes(deliverySource) || !!order.delivery_info?.address_street;

      // Endereco (somente delivery) com wrap
      const addr = order.delivery_info || {};
      if (isDeliveryOrder && addr.address_street) {
        let street = addr.address_street;
        if (addr.address_number) street += ', ' + addr.address_number;
        await printWrapped(street);
        if (addr.address_neighborhood) await printWrapped(addr.address_neighborhood);
        if (addr.address_complement) await printWrapped('Compl: ' + addr.address_complement);
      }

      // Pagamento (somente delivery ou COD)
      const paymentMode = isDeliveryOrder ? (order.payment_mode || '') : '';
      const paymentStatus = order.payment_status || '';
      const paymentType = order.ifood_details?.payment_type || order.cod_payment_type || '';

      if (paymentMode === 'ONLINE') {
        await this.printLine('PAGAMENTO ONLINE');
        if (paymentStatus === 'PAGO') {
          await this.printLine('>> PAGO <<');
        } else if (paymentStatus === 'AGUARDANDO_PAGAMENTO') {
          await this.printLine('>> AGUARDANDO PGTO <<');
        } else {
          await printWrapped('Status: ' + paymentStatus);
        }
      } else if (paymentMode === 'COD') {
        const codType = order.cod_payment_type || '';
        if (codType === 'DINHEIRO') {
          await this.setBold(true);
          await this.printLine('>> DINHEIRO <<');
          await this.setBold(false);
          if (order.change_for) {
            await this.setTextSize(2, 1);
            await this.setBold(true);
            // Double-width: 21 cols efetivos — dividir em 2 linhas para seguranca
            await this.printLine('TROCO PARA:');
            await this.printLine(this.formatCurrency(order.change_for));
            await this.setTextSize(1, 1);
            await this.setBold(false);
          } else {
            await this.printLine('SEM TROCO');
          }
        } else if (codType === 'CARTAO_ENTREGA') {
          await this.setBold(true);
          await this.printLine('>> CARTAO ENTREGA <<');
          await this.setBold(false);
        } else {
          await this.printLine('PGTO NA ENTREGA');
        }
      } else if (paymentType) {
        await printWrapped('Pagamento: ' + paymentType);
      }

      await this.printLine('-'.repeat(COLS));

      // Itens
      await this.setBold(true);
      await this.printLine('ITENS');
      await this.setBold(false);

      if (order.items && order.items.length > 0) {
        for (const item of order.items) {
          const qty = item.quantity || 1;
          const name = item.product_name || item.name || 'Item';
          const price = item.total_price || item.price || 0;
          const itemLabel = qty + 'x ' + name;
          const priceStr = this.formatCurrency(price);

          if (itemLabel.length + priceStr.length + 1 <= COLS) {
            // Cabe na mesma linha
            await this.printSpacedLine(itemLabel, priceStr, COLS);
          } else {
            // Nome longo: truncar para caber com preco na mesma linha
            const maxName = COLS - priceStr.length - 2;
            if (maxName > 5) {
              const truncName = itemLabel.length > maxName ? itemLabel.substring(0, maxName) : itemLabel;
              await this.printSpacedLine(truncName, priceStr, COLS);
            } else {
              // Caso extremo: imprimir nome e preco em linhas separadas
              await printWrapped(itemLabel);
              await this.printSpacedLine('', priceStr, COLS);
            }
          }

          // Obs do item com wrap
          if (item.notes) {
            const noteLines = wrapLines('  Obs: ' + item.notes, COLS);
            for (const nl of noteLines) await this.printLine(nl);
          }

          // Modificadores com wrap
          if (item.modifiers && item.modifiers.length > 0) {
            for (const mod of item.modifiers) {
              const modName = mod.option_name || mod.name || mod;
              const modPrice = mod.price_modifier ? ' (+' + this.formatCurrency(mod.price_modifier) + ')' : '';
              const modText = '  + ' + modName + modPrice;
              const modLines = wrapLines(modText, COLS);
              for (const ml of modLines) await this.printLine(ml);
            }
          }
        }
      }

      await this.printLine('-'.repeat(COLS));

      // Totais
      if (order.subtotal && order.subtotal !== order.total) {
        await this.printSpacedLine('Subtotal:', this.formatCurrency(order.subtotal), COLS);
      }

      if (order.discount && order.discount > 0) {
        await this.printSpacedLine('Desconto:', '-' + this.formatCurrency(order.discount), COLS);
      }

      if (order.delivery_fee && order.delivery_fee > 0) {
        await this.printSpacedLine('Taxa entrega:', this.formatCurrency(order.delivery_fee), COLS);
      }

      await this.printLine('-'.repeat(COLS));
      await this.setBold(true);
      await this.printSpacedLine('TOTAL:', this.formatCurrency(order.total || 0), COLS);
      await this.setBold(false);

      // Observacoes do pedido com word-wrap
      if (order.notes) {
        const cleanNotes = (order.notes || '')
          .replace(/\|\s*Endereco:.*$/gim, '')
          .replace(/Pedido Keeta #\d+ - /g, '')
          .trim();
        if (cleanNotes) {
          const obsLines = wrapLines('Obs: ' + cleanNotes, COLS);
          for (const ol of obsLines) await this.printLine(ol);
        }
      }

      await this.setAlignment(1);
      await this.printLine('Obrigado pela preferencia!');

      await this.feedLines(3);
      await this.cut();

      return { success: true };
    } catch (error) {
      console.error('Erro ao imprimir pedido:', error);
      throw error;
    }
  }

  // ============ VIA COZINHA ============
  async printKitchenOrder(order, storeName = 'Hit Grill') {
    try {
      await this.init();

      await this.setAlignment(1);
      await this.setTextSize(2, 2);
      await this.setBold(true);
      await this.printLine('COZINHA');

      await this.setTextSize(1, 1);
      await this.setBold(false);
      await this.printLine(storeName);

      const displayId = order.order_number ? '#' + order.order_number : '#' + (order.id || '').substring(0, 8).toUpperCase();
      await this.setTextSize(2, 2);
      await this.setBold(true);
      await this.printLine(displayId);
      await this.setTextSize(1, 1);
      await this.setBold(false);

      const origin = order.origin || order.order_channel || order.external_source || 'LOJA';
      const time = new Date(order.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
      await this.setTextSize(2, 1);
      await this.setBold(true);
      await this.printLine(origin + ' - ' + time);
      await this.setBold(false);
      await this.setTextSize(1, 1);

      if (order.customer_name) {
        await this.setTextSize(2, 1);
        await this.setBold(true);
        await this.printLine(order.customer_name);
        await this.setBold(false);
        await this.setTextSize(1, 1);
      }

      const kitchenPaymentMode = order.payment_mode || '';
      const kitchenCodType = order.cod_payment_type || '';
      if (kitchenPaymentMode === 'ONLINE') {
        await this.setTextSize(2, 1);
        await this.setBold(true);
        if (order.payment_status === 'PAGO') {
          await this.printLine('** PAGO ONLINE **');
        } else {
          await this.printLine('!! AGUARDANDO PG !!');
        }
        await this.setBold(false);
        await this.setTextSize(1, 1);
      } else if (kitchenPaymentMode === 'COD') {
        await this.setTextSize(2, 1);
        await this.setBold(true);
        if (kitchenCodType === 'DINHEIRO') {
          await this.printLine('** DINHEIRO **');
          if (order.change_for) {
            await this.printLine('TROCO: ' + this.formatCurrency(order.change_for));
          }
        } else if (kitchenCodType === 'CARTAO_ENTREGA') {
          await this.printLine('CARTAO ENTREGA');
        } else {
          await this.printLine('PG NA ENTREGA');
        }
        await this.setBold(false);
        await this.setTextSize(1, 1);
      }

      await this.setAlignment(0);
      await this.printSeparator('=');

      if (order.items && order.items.length > 0) {
        for (const item of order.items) {
          const qty = item.quantity || 1;
          const name = item.product_name || item.name || 'Item';

          await this.setTextSize(2, 1);
          await this.setBold(true);
          await this.printLine(qty + 'x ' + name);
          await this.setTextSize(1, 1);
          await this.setBold(false);

          const area = item.production_area || 'COZINHA';
          const prepTime = item.prep_time_minutes || 15;
          await this.printLine('  ' + area + ' | ' + prepTime + ' min');

          if (item.modifiers && item.modifiers.length > 0) {
            for (const mod of item.modifiers) {
              const modName = mod.option_name || mod.name || mod;
              await this.printLine('  + ' + modName);
            }
          }

          if (item.notes) {
            await this.setBold(true);
            await this.printLine('  OBS: ' + item.notes);
            await this.setBold(false);
          }
        }
      }

      await this.printSeparator('=');

      if (order.notes) {
        await this.setTextSize(2, 1);
        await this.setBold(true);
        await this.printLine('OBS GERAL:');
        await this.setTextSize(1, 1);
        await this.setBold(false);
        await this.printLine(order.notes);
      }

      await this.setAlignment(1);
      await this.setTextSize(1, 1);
      await this.printLine('Impresso: ' + new Date().toLocaleString('pt-BR'));

      await this.feedLines(2);
      await this.cut();

      return { success: true };
    } catch (error) {
      console.error('Erro ao imprimir comanda cozinha:', error);
      throw error;
    }
  }

  // ============ VIA KEETA ============
  async printKeetaOrder(order, storeName = 'Hit Grill') {
    try {
      await this.init();

      await this.setAlignment(1);
      await this.setTextSize(2, 2);
      await this.setBold(true);
      await this.printLine('keeta');

      await this.setTextSize(1, 1);
      await this.setBold(false);
      await this.printLine(storeName);

      await this.setBold(true);
      await this.printLine('[  Entrega propria  ]');
      await this.setBold(false);

      const displayId = order.keeta_order_no || order.display_id || (order.external_id ? order.external_id.slice(-4) : '') || (order.id ? order.id.substring(0, 8).toUpperCase() : '');
      await this.setTextSize(2, 2);
      await this.setBold(true);
      await this.printLine('#' + displayId);
      await this.setTextSize(1, 1);
      await this.setBold(false);

      const createdAt = new Date(order.created_at);
      const estimatedStart = new Date(createdAt.getTime() + 30 * 60000);
      const estimatedEnd = new Date(createdAt.getTime() + 40 * 60000);
      const timeFormat = { hour: '2-digit', minute: '2-digit' };
      await this.setBold(true);
      await this.printLine('Entrega estimada');
      await this.printLine(estimatedStart.toLocaleTimeString('pt-BR', timeFormat) + '-' + estimatedEnd.toLocaleTimeString('pt-BR', timeFormat));
      await this.setBold(false);

      await this.setAlignment(0);
      await this.setBold(true);
      await this.printLine('Endereco:');
      await this.setBold(false);

      let address = '';
      if (order.delivery_address) {
        try {
          const addr = typeof order.delivery_address === 'string' ? JSON.parse(order.delivery_address) : order.delivery_address;
          const parts = [];
          if (addr.street) parts.push(addr.street);
          if (addr.streetNumber || addr.number) parts.push(addr.streetNumber || addr.number);
          if (addr.neighborhood || addr.district) parts.push(addr.neighborhood || addr.district);
          if (addr.city) parts.push(addr.city);
          address = parts.join(', ');
        } catch(e) {
          address = String(order.delivery_address).substring(0, 100);
        }
      }
      if (address) {
        await this.printLine(address.substring(0, this.charsPerLine));
        if (address.length > this.charsPerLine) {
          await this.printLine(address.substring(this.charsPerLine, this.charsPerLine * 2));
        }
      }

      await this.printLine('_'.repeat(this.charsPerLine));
      await this.printLine('');

      await this.setAlignment(1);
      await this.setBold(true);
      await this.printLine(order.customer_name || 'Cliente');
      await this.setBold(false);
      await this.printLine('Pago');

      const itemCount = (order.items && order.items.length) || 0;
      await this.printLine(itemCount + (itemCount === 1 ? ' item' : ' itens'));

      await this.printSeparator('-');

      const rawNotes = order.notes || '';
      const cleanNotes = rawNotes.replace(/Pedido Keeta #\d+ - /g, '').trim();
      if (cleanNotes) {
        await this.setAlignment(0);
        await this.setBold(true);
        await this.printLine('Obs. do pedido');
        await this.setBold(false);
        await this.printLine(cleanNotes);
      }

      await this.setAlignment(0);
      await this.setBold(true);
      await this.printSpacedLine('Qtd    Itens', 'Preco(R$)');
      await this.setBold(false);

      let itemsTotal = 0;
      if (order.items && order.items.length > 0) {
        for (const item of order.items) {
          const qty = item.quantity || 1;
          const name = item.product_name || item.name || 'Item';
          const price = parseFloat(item.total_price || item.unit_price || 0);
          itemsTotal += price;
          await this.printSpacedLine(qty + 'x    ' + name.substring(0, 25), price.toFixed(2));
        }
      }

      await this.printLine('_'.repeat(this.charsPerLine));

      await this.printSpacedLine('Total do item', itemsTotal.toFixed(2));

      const deliveryFee = parseFloat(order.delivery_fee || 15);
      await this.printSpacedLine('Taxa de entrega', deliveryFee.toFixed(2));

      const serviceFee = 0.99;
      await this.printSpacedLine('Taxa de servico', serviceFee.toFixed(2));

      await this.printLine('_'.repeat(this.charsPerLine));

      const total = parseFloat(order.total || (itemsTotal + deliveryFee + serviceFee));
      await this.setBold(true);
      await this.printSpacedLine('Valor pago pelo', '');
      await this.printSpacedLine('cliente', 'R$ ' + total.toFixed(2));
      await this.setBold(false);

      await this.printLine('_'.repeat(this.charsPerLine));
      await this.printLine('');

      await this.setAlignment(0);
      const orderId = order.external_id || order.keeta_order_id || order.id || '';
      await this.printLine('Numero do pedido ' + orderId);
      await this.printLine('Pedido em ' + createdAt.toLocaleDateString('pt-BR') + ' ' + createdAt.toLocaleTimeString('pt-BR', timeFormat));

      await this.setAlignment(1);
      await this.setTextSize(2, 1);
      await this.setBold(true);
      await this.printLine('OBRIGADO');
      await this.setTextSize(1, 1);
      await this.setBold(false);

      await this.feedLines(1);
      await this.cut();

      return { success: true };
    } catch (error) {
      console.error('Erro ao imprimir pedido Keeta:', error);
      throw error;
    }
  }

  // ============ FASE 4: 3ª VIA DE EXPEDIÇÃO / ENTREGA ============
  /**
   * Imprime etiqueta logística de expedição.
   * Foco: dados de entrega para o entregador/expedição.
   * NÃO exibe valores financeiros (exceto total para conferência).
   */
  async printExpeditionLabel(order, storeName = 'Hit Grill') {
    try {
      await this.init();

      // Cabecalho compacto
      await this.setAlignment(1);
      await this.setBold(true);
      await this.printLine('EXPEDICAO - ' + storeName);
      await this.setBold(false);

      // Numero do pedido + canal na mesma area
      const displayId = order.display_id || order.order_number || order.id?.substring(0, 8).toUpperCase() || '';
      const channel = order.external_source || order.order_channel || order.origin || 'LOJA';
      await this.setTextSize(2, 1);
      await this.setBold(true);
      await this.printLine('#' + displayId + ' ' + channel);
      await this.setTextSize(1, 1);
      await this.setBold(false);

      // Data/hora
      const time = new Date(order.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
      await this.printLine(new Date(order.created_at).toLocaleDateString('pt-BR') + ' ' + time);

      await this.setAlignment(0);
      await this.printSeparator('=');

      // Cliente + telefone compacto
      if (order.customer_name) {
        await this.setBold(true);
        await this.printLine(order.customer_name);
        await this.setBold(false);
      }
      const phone = order.customer_phone || order.delivery_info?.customer_phone || '';
      if (phone) {
        await this.printLine('Tel: ' + phone);
      }

      // Endereco compacto (direto abaixo do cliente)
      const addr = order.delivery_info || {};
      if (addr.address_street) {
        let street = addr.address_street;
        if (addr.address_number) street += ', ' + addr.address_number;
        await this.setBold(true);
        await this.printLine(street);
        await this.setBold(false);
        if (addr.address_complement) await this.printLine('Compl: ' + addr.address_complement);
        if (addr.address_reference) {
          await this.setBold(true);
          await this.printLine('REF: ' + addr.address_reference);
          await this.setBold(false);
        }
        if (addr.address_neighborhood || addr.neighborhood) {
          await this.printLine('Bairro: ' + (addr.address_neighborhood || addr.neighborhood));
        }
      }

      // Endereco Keeta
      if (!addr.address_street && order.delivery_address) {
        try {
          const parsedAddr = typeof order.delivery_address === 'string' ? JSON.parse(order.delivery_address) : order.delivery_address;
          const parts = [];
          if (parsedAddr.street) parts.push(parsedAddr.street);
          if (parsedAddr.streetNumber || parsedAddr.number) parts.push(parsedAddr.streetNumber || parsedAddr.number);
          if (parts.length > 0) {
            await this.setBold(true);
            await this.printLine(parts.join(', '));
            await this.setBold(false);
          }
          if (parsedAddr.complement) await this.printLine('Compl: ' + parsedAddr.complement);
          if (parsedAddr.reference) {
            await this.setBold(true);
            await this.printLine('REF: ' + parsedAddr.reference);
            await this.setBold(false);
          }
          if (parsedAddr.neighborhood || parsedAddr.district) await this.printLine('Bairro: ' + (parsedAddr.neighborhood || parsedAddr.district));
        } catch(e) {
          await this.printLine(String(order.delivery_address).substring(0, 100));
        }
      }

      // Pagamento (entregador precisa saber)
      const paymentMode = order.payment_mode || '';
      if (paymentMode === 'COD') {
        await this.setBold(true);
        const codType = order.cod_payment_type || '';
        if (codType === 'DINHEIRO') {
          await this.printLine('>> COBRAR DINHEIRO <<');
          if (order.change_for) {
            await this.printLine('TROCO P/ ' + this.formatCurrency(order.change_for));
          }
        } else if (codType === 'CARTAO_ENTREGA') {
          await this.printLine('>> MAQUININHA <<');
        } else if (['VEROCARD', 'SODEXO', 'VR', 'PLUXE'].includes(codType)) {
          await this.printLine('>> ' + codType + ' <<');
        } else {
          await this.printLine('>> PG NA ENTREGA <<');
        }
        await this.setBold(false);
      } else if (paymentMode === 'ONLINE') {
        await this.setBold(true);
        await this.printLine('PAGO ONLINE - NAO COBRAR');
        await this.setBold(false);
      }

      // OBS de entrega (somente delivery-specific, NAO obs geral)
      const deliveryNotes = order.delivery_notes || addr.delivery_notes || '';
      if (deliveryNotes) {
        await this.setBold(true);
        await this.printLine('OBS: ' + deliveryNotes);
        await this.setBold(false);
      }

      await this.printSeparator('=');

      // Itens compactos
      if (order.items && order.items.length > 0) {
        for (const item of order.items) {
          const qty = item.quantity || 1;
          const name = item.product_name || item.name || 'Item';
          await this.printLine(qty + 'x ' + name);
          if (item.modifiers && item.modifiers.length > 0) {
            for (const mod of item.modifiers) {
              await this.printLine('  + ' + (mod.option_name || mod.name));
            }
          }
        }
      }

      await this.printSeparator('=');

      // Total compacto
      await this.setBold(true);
      await this.printSpacedLine('TOTAL:', this.formatCurrency(order.total || 0));
      await this.setBold(false);

      await this.setAlignment(1);
      await this.printLine(new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }));

      await this.cut();

      return { success: true };
    } catch (error) {
      console.error('Erro ao imprimir etiqueta expedição:', error);
      throw error;
    }
  }

  // ============ FASE 1: ORQUESTRADOR ÚNICO DE IMPRESSÃO ============
  /**
   * Função central que imprime múltiplas vias em sequência segura.
   *
   * @param {Object} order - Dados do pedido
   * @param {Object} options - Configuração de vias:
   *   kitchen  {boolean} - Imprimir via cozinha
   *   full     {boolean} - Imprimir via completa/restaurante
   *   expedition {boolean} - Imprimir 3ª via de expedição
   *   auto     {boolean} - Auto-detectar vias por canal/origem
   *   storeName {string} - Nome da loja
   */
  async printAllReceipts(order, options = {}) {
    const {
      kitchen = false,
      full = false,
      expedition = false,
      auto = false,
      storeName = 'Hit Grill',
    } = options;

    const channel = (order.external_source || order.order_channel || order.origin || '').toUpperCase();
    const isKeeta = channel === 'KEETA';
    const isDelivery = order.origin === 'DELIVERY' || !!order.delivery_info;

    // Montar lista de vias a imprimir
    const receipts = [];

    if (auto) {
      // Modo automático: decide por canal
      if (isKeeta) {
        receipts.push({ type: 'keeta', fn: () => this.printKeetaOrder(order, storeName) });
      } else {
        receipts.push({ type: 'kitchen', fn: () => this.printKitchenOrder(order, storeName) });
        receipts.push({ type: 'full', fn: () => this.printOrder(order, storeName) });
      }
      // 3ª via de expedição para delivery
      if (isDelivery) {
        receipts.push({ type: 'expedition', fn: () => this.printExpeditionLabel(order, storeName) });
      }
    } else {
      // Modo manual: imprime exatamente o que foi pedido
      if (kitchen) {
        receipts.push({ type: 'kitchen', fn: () => this.printKitchenOrder(order, storeName) });
      }
      if (full) {
        if (isKeeta) {
          receipts.push({ type: 'keeta', fn: () => this.printKeetaOrder(order, storeName) });
        } else {
          receipts.push({ type: 'full', fn: () => this.printOrder(order, storeName) });
        }
      }
      if (expedition) {
        receipts.push({ type: 'expedition', fn: () => this.printExpeditionLabel(order, storeName) });
      }
    }

    if (receipts.length === 0) {
      console.log('[Printer] Nenhuma via selecionada para impressão.');
      return { success: true, printed: [] };
    }

    console.log(`[Printer] Imprimindo ${receipts.length} via(s): ${receipts.map(r => r.type).join(', ')}`);

    // Usar fila para impressão sequencial segura
    const printed = [];
    for (const receipt of receipts) {
      try {
        await this.enqueuePrint(receipt.fn);
        printed.push(receipt.type);
        console.log(`[Printer] Via '${receipt.type}' impressa com sucesso.`);
      } catch (error) {
        console.error(`[Printer] Erro ao imprimir via '${receipt.type}':`, error);
      }
    }

    return { success: printed.length > 0, printed };
  }

  // ============ IMPRIMIR TESTE ============
  async printTest() {
    try {
      await this.init();

      await this.setAlignment(1);
      await this.setTextSize(2, 2);
      await this.setBold(true);
      await this.printLine('TESTE');

      await this.setTextSize(1, 1);
      await this.setBold(false);
      await this.printLine('');
      await this.printLine('Epson TM-T88V');
      await this.printLine('Conexao via WebUSB');
      await this.printLine('');

      await this.setAlignment(0);
      await this.printSeparator();

      await this.printSpacedLine('Item 1', 'R$ 10,00');
      await this.printSpacedLine('Item 2', 'R$ 15,50');
      await this.printSpacedLine('Item 3', 'R$ 8,25');

      await this.printSeparator();

      await this.setBold(true);
      await this.printSpacedLine('TOTAL', 'R$ 33,75');
      await this.setBold(false);

      await this.printLine('');
      await this.setAlignment(1);
      await this.printLine(new Date().toLocaleString('pt-BR'));
      await this.printLine('');

      const settings = this.getSettings();
      await this.printLine('Config: ' + settings.source);
      await this.printLine('Auto-print: ' + (settings.autoPrint ? 'ATIVO' : 'INATIVO'));
      await this.printLine('Copias Rest: ' + settings.restaurantCopies);
      await this.printLine('Copias Deliv: ' + settings.deliveryCopies);

      await this.printLine('');
      await this.printLine('Teste concluido!');

      await this.feedLines(1);
      await this.cut();

      return { success: true };
    } catch (error) {
      console.error('Erro no teste de impressao:', error);
      throw error;
    }
  }

  async openCashDrawer() {
    try {
      await this.send([this.ESC, 0x70, 0, 100, 100]);
      return { success: true };
    } catch (error) {
      console.error('Erro ao abrir gaveta:', error);
      throw error;
    }
  }

  // ============ MÉTODOS LEGADOS (compatibilidade) ============

  async printOrderByChannel(order, storeName = 'Hit Grill') {
    const channel = (order.external_source || order.order_channel || '').toUpperCase();

    for (let i = 0; i < this.copies; i++) {
      if (channel === 'KEETA') {
        await this.printKeetaOrder(order, storeName);
      } else {
        await this.printOrder(order, storeName);
      }

      if (i < this.copies - 1) {
        await new Promise(r => setTimeout(r, 500));
      }
    }
  }

  async printKitchenOrderWithCopies(order, storeName = 'Hit Grill') {
    for (let i = 0; i < this.copies; i++) {
      await this.printKitchenOrder(order, storeName);
      if (i < this.copies - 1) {
        await new Promise(r => setTimeout(r, 500));
      }
    }
  }
}

// Singleton
export const thermalPrinter = new ThermalPrinterService();

// Iniciar auto-reconnect e carregar settings
thermalPrinter.startAutoReconnect();
thermalPrinter.loadSettings();

export function useThermalPrinter() {
  return thermalPrinter;
}
